import { useState } from 'react';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { flightApi, type ManualFlightOperationCreateRequest } from '@/services/api';

const DEFAULT_FLIGHT_DATE = new Date().toISOString().slice(0, 10);

export function ManualFlightOperationPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ManualFlightOperationCreateRequest>({
    operationType: 'scheduled',
    flightDate: DEFAULT_FLIGHT_DATE,
    flightNo: '',
    departure: '',
    arrival: '',
    aircraftType: '',
    aircraftRegistration: '',
    crewRole: '',
  });

  const update = <K extends keyof ManualFlightOperationCreateRequest>(key: K, value: ManualFlightOperationCreateRequest[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await flightApi.createManualOperation(form);
      toast({ title: '已创建飞行记录，可开始写笔记' });
      navigate(`/notes/new?flightOperation=${response.data.data.id}`);
    } catch (error: any) {
      toast({
        title: error?.response?.data?.message || '创建失败，请检查填写内容',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">手动补录飞行</h1>
          <p className="mt-1 text-sm text-gray-500">没有 E-Flight 任务时，先建立最小飞行上下文，再记录随手笔记。</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/flights')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          返回航班
        </Button>
      </div>

      <form className="space-y-5 rounded-lg border bg-white p-5 shadow-sm" onSubmit={(event) => void submit(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            飞行日期 <span className="text-red-500">*</span>
            <Input required type="date" value={form.flightDate} onChange={(event) => update('flightDate', event.target.value)} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            飞行类型
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.operationType} onChange={(event) => update('operationType', event.target.value as ManualFlightOperationCreateRequest['operationType'])}>
              <option value="scheduled">计划航班</option>
              <option value="ferry">调机</option>
              <option value="training">训练</option>
              <option value="simulator">模拟机</option>
              <option value="other">其他</option>
            </select>
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            航班号
            <Input maxLength={50} placeholder="例如 MU5101" value={form.flightNo} onChange={(event) => update('flightNo', event.target.value)} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            机型
            <Input maxLength={50} placeholder="例如 A320" value={form.aircraftType} onChange={(event) => update('aircraftType', event.target.value)} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            出发机场
            <Input maxLength={10} placeholder="例如 PVG" value={form.departure} onChange={(event) => update('departure', event.target.value.toUpperCase())} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            到达机场
            <Input maxLength={10} placeholder="例如 PEK" value={form.arrival} onChange={(event) => update('arrival', event.target.value.toUpperCase())} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            机号
            <Input maxLength={50} value={form.aircraftRegistration} onChange={(event) => update('aircraftRegistration', event.target.value)} />
          </label>
          <label className="grid gap-1.5 text-sm font-medium text-gray-700">
            岗位
            <Input maxLength={50} placeholder="例如 副驾驶" value={form.crewRole} onChange={(event) => update('crewRole', event.target.value)} />
          </label>
        </div>
        <p className="text-xs text-gray-500">这些信息可后续与 E-Flight 或 FSOP 数据匹配；它们不会替代外部来源快照。</p>
        <Button className="w-full" disabled={saving} type="submit">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BookOpen className="mr-2 h-4 w-4" />}
          创建并写笔记
        </Button>
      </form>
    </div>
  );
}
